/* angular */
import { Injectable, Injector } from '@angular/core';
/* end angular */

/* rxjs */
import { share, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
/* end rxjs */

/* sixense */
import { BydLocalStorage } from '@sixense/ds-lib-utils';
import { BydTokenService } from '@sixense/byd-core-service';
/* end sixense */

/* services */
import { BydAuthAbstractService } from '../shared/services/byd-auth-abstract.service';
/* end services */

/* models */
import { BydUserAccount } from '../models/byd-user-account';
import { BydUserAccountLogin } from '../models/byd-user-account-login';
import { BydLogin } from '../models/byd-login';
import { BydRefreshLogin } from '../models/byd-refresh-login';
/* end models */

/* interfaces */
import { BydUserAccountLoginInterface } from '../interfaces/byd-user-account-login.interface';
/* end interfaces */

/* enums */
import { BydAuthStorageKeysEnum } from '../enums/byd-auth-storage-keys.enum';
import { BydAuthApiPathsEnum } from '../enums/byd-auth-api-paths.enum';
/* end enums */

@Injectable({
  providedIn: 'root',
})
export class BydAuthService extends BydAuthAbstractService {
  /**
   * The abstract endpoint path.
   */
  public path: string;

  /**
   * The login status.
   */
  private _isLoggedIn = false;

  /**
   * The current logged user account.
   */
  private _currentAccount: BydUserAccount = JSON.parse(
    localStorage.getItem(BydAuthStorageKeysEnum.CURRENT_ACCOUNT_KEY)
  );

  /**
   * The isLoggedIn getter.
   *
   * @returns boolean
   */
  public get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  /**
   * The isLoggedIn setter.
   *
   * @param value the isLoggedIn status.
   */
  public set isLoggedIn(value: boolean) {
    this._isLoggedIn = value;
  }

  /**
   * The current user account getter.
   *
   * @returns `BydUserAccount`
   */
  public get currentAccount(): BydUserAccount {
    return this._currentAccount;
  }

  /**
   * The current user account setter.
   *
   * @param value the current account instance.
   */
  public set currentAccount(value: BydUserAccount) {
    localStorage.setItem(BydAuthStorageKeysEnum.CURRENT_ACCOUNT_KEY, JSON.stringify(value));
    this._currentAccount = value;
  }

  /**
   * Creates an instance of BydAuthService.
   *
   * @param injector creates a service injector instance
   * @param bydTokenService the byd token service
   */
  constructor(protected injector: Injector, protected bydTokenService: BydTokenService) {
    super(injector);
    if (
      this.bydConfigService.headers.get(BydAuthStorageKeysEnum.AUTHORIZATION_KEY) &&
      this.bydTokenService.refreshToken
    ) {
      this.isLoggedIn = true;
    }
  }

  /**
   * Login the user and get the token from response.
   *
   * @param login the user login
   * @param password the user password
   * @returns Observable<BydUserAccountLogin>
   */
  public login(login: string, password: string): Observable<BydUserAccountLogin> {
    this.path = BydAuthApiPathsEnum.USERS_AUTHENTIFY_V2;
    const body = new BydLogin({ login, pwd: password, scope: this.bydConfigService.injectedApiToken.apiScope });
    return this.postModel<BydUserAccountLogin>({ body: JSON.stringify(body) }).pipe(
      tap((response) => {
        if (response && response.isLoggedIn && response.token && response.account) {
          this.currentAccount = new BydUserAccount(response.account);
          this.bydTokenService.updateToken(response.token, response.refreshToken);
          this.isLoggedIn = true;
        }
      })
    );
  }

  /**
   * Refresh login with an refreshToken from BydTokenService.
   *
   * @returns Observable<BydUserAccountLogin>
   */
  public refreshLogin(): Observable<BydUserAccountLogin> {
    this.path = BydAuthApiPathsEnum.AUTH_REFRESH;
    return this.postModel<BydUserAccountLogin>({
      body: new BydRefreshLogin({ refreshToken: this.bydTokenService.refreshToken }),
    }).pipe(
      tap((response) => {
        if (response?.token && response?.refreshToken) {
          this.bydTokenService.updateToken(response.token, response.refreshToken);
          this.isLoggedIn = true;
        }
      }),
      share()
    );
  }

  /**
   * Logout removes the current logged account & token.
   */
  public logout(): void {
    BydLocalStorage.removeItem(BydAuthStorageKeysEnum.CURRENT_ACCOUNT_KEY);
    this.bydTokenService.removeToken();
    this.currentAccount = new BydUserAccount();
    this.isLoggedIn = false;
  }

  /**
   * Creates an instance of BydUserAccountLogin.
   *
   * @param jsonData the user account login data.
   * @returns BydUserAccountLogin
   */
  public createNewInstance(jsonData: BydUserAccountLoginInterface): BydUserAccountLogin {
    return new BydUserAccountLogin(jsonData);
  }
}
